import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FileText, Download, ArrowUpRight, Eye, ArrowRight } from "lucide-react";
import { resolveUrl } from "../lib/api";
import { colorFor } from "../lib/categories";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const ProjectChapter = ({ index, category, image, projects, onView }) => {
  const num = String(index + 1).padStart(2, "0");
  const color = colorFor(category);
  return (
    <section
      data-testid={`chapter-${num}`}
      className="border-t border-[var(--border)] py-20 sm:py-28"
    >
      {/* chapter header */}
      <div className="mx-auto max-w-[1600px] px-5 sm:px-10">
        <div className="mb-14 grid grid-cols-12 items-end gap-6">
          <div className="col-span-12 lg:col-span-8">
            <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em]" style={{ color }}>
              <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
              Chapter {num}
            </p>
            <h2 className="mt-4 font-display text-4xl font-black uppercase leading-[0.9] tracking-tighter sm:text-5xl lg:text-7xl">
              {category}
            </h2>
            <div className="mt-6 h-1.5 w-24 rounded-full" style={{ background: color }} />
          </div>
          {image && (
            <div className="col-span-12 hidden overflow-hidden lg:col-span-4 lg:block">
              <div className="aspect-[16/9] overflow-hidden rounded-sm border-b-4" style={{ borderColor: color }}>
                <img
                  src={image}
                  alt={category}
                  loading="lazy"
                  className="h-full w-full object-cover grayscale transition-all duration-700 hover:grayscale-0"
                />
              </div>
            </div>
          )}
        </div>

        {/* projects */}
        <div className="flex flex-col">
          {projects.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`project-${p.id}`}
              custom={i}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              className="grid grid-cols-12 gap-6 border-b border-[var(--border)] py-12 first:border-t"
            >
              <div className="col-span-12 md:col-span-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--text-2)]">
                  {p.subtitle}
                </p>
                <h3 className="mt-3 font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl">
                  {p.title}
                </h3>
                <div className="mt-5 flex flex-wrap gap-2">
                  {p.tags.map((t) => (
                    <span
                      key={t}
                      className="flex items-center gap-1.5 rounded-full border border-[var(--border)] px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-[var(--text-2)] transition-colors hover:text-[var(--text)]"
                    >
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              <div className="col-span-12 md:col-span-8">
                <p className="max-w-2xl text-base leading-relaxed text-[var(--text-2)] sm:text-lg">
                  {p.description}
                </p>
                <div className="mt-7 flex flex-wrap items-center gap-3">
                  {p.case_study_slug && (
                    <Link
                      data-testid={`case-study-${p.id}`}
                      to={`/case-study/${p.case_study_slug}`}
                      style={{ background: color }}
                      className="group flex items-center gap-2 rounded-sm px-5 py-3 font-mono text-xs uppercase tracking-widest text-white transition-transform duration-300 hover:-translate-y-0.5"
                    >
                      Read Case Study{" "}
                      <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  )}
                  {p.pdf_url && (
                    <>
                      <button
                        data-testid={`view-pdf-${p.id}`}
                        onClick={() => onView(p)}
                        className="group flex items-center gap-2 rounded-sm bg-[var(--text)] px-5 py-3 font-mono text-xs uppercase tracking-widest text-[var(--bg)] transition-transform duration-300 hover:-translate-y-0.5"
                      >
                        <Eye size={14} /> View PDF
                      </button>
                      <a
                        data-testid={`download-pdf-${p.id}`}
                        href={resolveUrl(p.pdf_url)}
                        download
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 rounded-sm border border-[var(--border)] px-5 py-3 font-mono text-xs uppercase tracking-widest transition-colors hover:border-[var(--accent)] hover:text-[var(--accent)]"
                      >
                        <Download size={14} /> Download
                      </a>
                    </>
                  )}
                  {p.external_url && (
                    <a
                      data-testid={`external-${p.id}`}
                      href={p.external_url}
                      target="_blank"
                      rel="noreferrer"
                      className="group flex items-center gap-2 rounded-sm border border-[var(--border)] px-5 py-3 font-mono text-xs uppercase tracking-widest transition-colors hover:border-[var(--accent)] hover:text-[var(--accent)]"
                    >
                      {p.external_label || "Open"}{" "}
                      <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  )}
                  {!p.pdf_url && !p.external_url && !p.case_study_slug && (
                    <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[var(--text-2)]">
                      <FileText size={13} /> Available on request
                    </span>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
